import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BarChart3, Star, TrendingUp, TrendingDown, AlertTriangle, ThumbsUp } from "lucide-react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock performance trend data
const performanceTrend = [
  { month: "Jul", score: 78 },
  { month: "Aug", score: 80 },
  { month: "Sep", score: 82 },
  { month: "Oct", score: 81 },
  { month: "Nov", score: 84 },
  { month: "Dec", score: 86 },
];

const riskTherapists = [
  { name: "Low Performance User", score: 55, risk: "Low Rating", trend: "down" },
  { name: "Burnout Risk User", score: 62, risk: "Burnout Signs", trend: "down" },
];

export default function AdminPerformance() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const avgPerformance = therapists.length > 0 
    ? (therapists.reduce((sum, t) => sum + (t.performance_score || 0), 0) / therapists.length).toFixed(1)
    : 0;
  
  const avgRating = therapists.length > 0
    ? (therapists.reduce((sum, t) => sum + (t.rating || 0), 0) / therapists.length).toFixed(1)
    : 0;

  const avgAcceptance = therapists.length > 0
    ? (therapists.reduce((sum, t) => sum + (t.acceptance_rate || 0), 0) / therapists.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-performance">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Performance</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{avgPerformance}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Rating</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{avgRating}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <ThumbsUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Acceptance</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{avgAcceptance}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">At Risk</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{riskTherapists.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trend */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Platform Performance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis domain={[70, 100]} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Avg. Score']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#F40d07" 
                  strokeWidth={3}
                  dot={{ fill: '#F40d07', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* At Risk Therapists */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Risk Flags
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {riskTherapists.map((th, i) => (
                <div key={i} className="p-4 bg-red-50 border border-red-100 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-red-100 text-red-700">
                          {th.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900">{th.name}</p>
                        <p className="text-sm text-red-600">{th.risk}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      <span className="font-bold text-red-600">{th.score}%</span>
                    </div>
                  </div>
                </div>
              ))}
              
              {riskTherapists.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <ThumbsUp className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                  <p>No therapists at risk!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Performance Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Performance</TableHead>
                <TableHead>Rating</TableHead>
                <TableHead>Acceptance</TableHead>
                <TableHead>Cancellation</TableHead>
                <TableHead>No-Show</TableHead>
                <TableHead>Sessions</TableHead>
                <TableHead>Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapists.slice(0, 10).map((th) => (
                <TableRow key={th.therapist_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={th.picture} />
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {th.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{th.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={th.performance_score} className="w-16 h-2" />
                      <span className="text-sm font-medium">{th.performance_score}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span>{th.rating?.toFixed(1)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={th.acceptance_rate >= 90 ? "text-emerald-600" : "text-amber-600"}>
                      {th.acceptance_rate}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={th.cancellation_rate <= 5 ? "text-emerald-600" : "text-red-600"}>
                      {th.cancellation_rate}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={th.no_show_rate <= 3 ? "text-emerald-600" : "text-red-600"}>
                      {th.no_show_rate}%
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">{th.total_sessions}</span>
                  </TableCell>
                  <TableCell>
                    {th.performance_score >= 80 ? (
                      <TrendingUp className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
